import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { InconsistencyReference, ReferenceType } from '@/../../backend/src/types/types';
import {
  type KimChronologyFactDetail,
  useGetFactDetailsForKimChronology,
} from '@/api/queries/useGetFactDetailsForKimChronology';
import { DocComparerGroup, type ComparerFactEntry } from '@/new/components/inconsistency/doc-comparer-group';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';

// The comparer can only open fact references, so the dialog (and its trigger)
// are only useful when at least one side resolves to a fact.
export function hasComparableReferences(references: InconsistencyReference[]): boolean {
  return references.some((r) => r.reference.refType === ReferenceType.Fact);
}

// Stable order: keep the first-seen name first so streamed refs don't reflow buckets.
function groupReferencesByName(refs: InconsistencyReference[]): { name: string; refs: InconsistencyReference[] }[] {
  const groups: { name: string; refs: InconsistencyReference[] }[] = [];
  for (const ref of refs) {
    const existing = groups.find((g) => g.name === ref.name);
    if (existing) existing.refs.push(ref);
    else groups.push({ name: ref.name, refs: [ref] });
  }
  return groups;
}

export function CompareEvidenceDialog({
  open,
  onOpenChange,
  references,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  references: InconsistencyReference[];
}) {
  const { matterId, subsetId } = useParams<{ matterId: string; subsetId: string }>();

  const groups = useMemo(() => groupReferencesByName(references), [references]);

  const factIds = useMemo(
    () => references.filter((r) => r.reference.refType === ReferenceType.Fact).map((r) => r.reference.id),
    [references],
  );

  const { data: factSummaries } = useGetFactDetailsForKimChronology(matterId, subsetId, factIds);

  const comparerFactsByGroup = useMemo(() => {
    const summaryMap = new Map<string, KimChronologyFactDetail>();
    factSummaries?.forEach((s) => summaryMap.set(s.id, s));

    return groups.map(({ name, refs }) => {
      const facts: ComparerFactEntry[] = refs
        .filter((r) => r.reference.refType === ReferenceType.Fact)
        .map((r) => summaryMap.get(r.reference.id))
        .filter((s): s is KimChronologyFactDetail => Boolean(s))
        .map((s) => ({
          fact: {
            id: s.id,
            description: s.description,
            locations: s.sources.map((source) => ({
              doc: {
                id: source.docId,
                file: { name: source.fileName },
                type: source.docType,
              },
            })),
          },
        }));
      return { name, facts };
    });
  }, [groups, factSummaries]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-9/10 max-h-none w-9/10 max-w-none gap-4 pb-3"
        onClick={(e) => e.stopPropagation()}
      >
        {comparerFactsByGroup.map(({ name, facts }) =>
          facts.length > 0 ? <DocComparerGroup key={name} title={name} facts={facts} /> : null,
        )}
      </DialogContent>
    </Dialog>
  );
}
